import React, { FC, useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { CombinedGraphQLErrors, ErrorLike } from '@apollo/client';
import AssetLoadManagementHeader from '../Header/AssetLoadManagementHeader';
import { AssetLoadFilters } from '../Filter/AssetLoadFilter';
import AssetLoadList from '../TableList/AssetLoadList';
import {
    GET_ASSETS_FOR_DOWNLOAD,
    GET_FILTERED_ASSET_LOAD_MANAGEMENTS,
    GET_ASSETS_FOR_EXPORT_DATA_DOWNLOAD,
    GET_ASSETS_FOR_MOB_DEMOB_FORM_DOWNLOAD,
} from '../../graphql/client/queries';
import {
    GetFilteredLoadManagements_getFilteredLoadManagements as Load,
    GetFilteredAssetLoadManagements,
    DeleteAssetLoads,
    CreateAssetLoadInput,
    CreateAssetLoad,
    EditAssetLoad,
    AssetLoadType,
    AddEquipmentsInput,
    GetExportDataForDownload,
    GetAssetLoadListToDownload,
    GetMobDemobFormExportDownload,
} from '../../types/schemaTypes';
import { Error } from '../_common';
import { useStoreContext } from '../App/StoreProvider';
import { useQuery } from '@apollo/client/react';
import './EquipmentManagement.scss';
import DeleteModal from '../JobProfile/DeleteModal';
import { ADD_EQUIPMENTS, CREATE_ASSET_LOAD, DELETE_ASSET_LOADS, EDIT_ASSET_LOAD } from '../../graphql/client/mutations';
import { LoadStatusEnum } from '../../types/LoadStatusEnum';
import * as AssetLoadManagementListActions from '../../store/actions/assetLoadManagementList';
import AddAssetLoadModal from '../JobProfile/Equipments/AddAssetLoadModal/AddAssetLoadModal';
import { ToastContainer, toast } from 'react-toastify';
import { useAuthContext } from '../Auth/AuthProvider';
import { PermissionEnum } from '../../types/PermissionEnum';
import { toastOpt } from '../../utils/helpers/toastHelper';
import { Parser } from '@json2csv/plainjs';
import { formatDateUtcToLocal } from '../../utils/helpers/datetimeHelper';
import AddEquipmentModal from '../AddEquipments/AddEquipmentsModal';
import { DropdownOption } from '../_common/SearchableDropdown/SearchableDropdown';
import moment from 'moment';

interface SelectedLoad {
    loadId: number | null;
}

// Do not change this value unless the associate backend value has been updated - (8/22/19) BS
const PAGE_SIZE = 50;

const EquipmentManagement: FC = (): React.ReactElement => {
    const isViewLoadEnabled = useAuthContext().userHasPermission(PermissionEnum.ViewAssetLoads);
    const isDeleteLoadEnabled = useAuthContext().userHasPermission(PermissionEnum.DeleteAssetLoad);
    const isAddLoadEnabled = useAuthContext().userHasPermission(PermissionEnum.AddAssetLoads);
    const isAssignLoadEnabled = useAuthContext().userHasPermission(PermissionEnum.AssignAssetLoads);
    const isAddEquipmentsEnabled = useAuthContext().userHasPermission(PermissionEnum.AdminEquipmentUI);

    const [selectedLoads, setSelectedLoads] = useState<SelectedLoad[]>([]);
    const [listHeight, setListHeight] = useState(window.screen.height);
    const [state, dispatch] = useStoreContext();
    const {
        assetLoadManagementList: { searchPhrase, filters },
    } = state;

    const [loadId, setLoadId] = useState<number>();
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isSearchOnLoadId, setIsSearchOnLoadId] = useState(true);

    const [modalError, setModalError] = useState<ErrorLike>();

    const [showAddModal, setShowAddModal] = useState(false);
    const [showAddEquipmentModal, setShowAddEquipmentModal] = useState(false);

    const [showFilter, setShowFilter] = useState(false);

    const abortController = new AbortController();
    const loadIds: any = [];
    useEffect(() => {
        dispatch(AssetLoadManagementListActions.resetFilterState());
    }, []);

    const onCheckAll = (checkedStatus: boolean, loadsList: any[]): void => {
        const result: SelectedLoad[] = [];
        if (checkedStatus === true) {
            loadsList.forEach((el: any): void => {
                if (el.status === LoadStatusEnum.CREATED || el.status === 'ReadyForDispatch') {
                    result.push({ loadId: el.loadId });
                }
            });
        }
        setSelectedLoads(result);
    };

    const context = React.useMemo(() => {
        return {
            fetchOptions: {
                signal: abortController.signal,
            },
        };
    }, []);

    const abortRequest = (): void => {
        abortController.abort();
    };

    const variables = {
        input: {
            jobIds: filters.jobIds,
            regionIds: filters.regionIds,
            statuses: filters.statuses,
            loadTypeIds: filters.loadTypeIds,
            drivers: filters.driverIds,
            carriers: filters.carrierIds,
            equipmentId: filters.equipmentId,
            equipmentTypeIds: filters.equipmentTypeIds,
            trailerTypeIds: filters.trailerTypeIds,
            atDestinationOutFrom: filters.fromDate,
            atDestinationOutTo: filters.toDate,
            isSearchOnLoadId: searchPhrase ? isSearchOnLoadId : true,
            search: searchPhrase,
            sortOrder: '',
        },
    };

    const { data, error, loading, fetchMore, refetch } = useQuery<GetFilteredAssetLoadManagements>(
        GET_FILTERED_ASSET_LOAD_MANAGEMENTS,
        {
            variables: variables,
            pollInterval: 300000,
            context: context,
            fetchPolicy: 'network-only',
        },
    );

    const assetLoadList: any[] = [];

    if (data && data.getFilteredAssetLoadManagements && data.getFilteredAssetLoadManagements.length !== 0) {
        data.getFilteredAssetLoadManagements.forEach((element: any | null): void => {
            if (element !== null) {
                assetLoadList.push(element);
            }
        });
    }
    const offset =
        data && data.getFilteredAssetLoadManagements ? data.getFilteredAssetLoadManagements.length / PAGE_SIZE : 0;
    let isEndOfResults = false;
    if (
        offset % 1 !== 0 ||
        (!isEndOfResults &&
            data &&
            data.getFilteredAssetLoadManagements &&
            data.getFilteredAssetLoadManagements.length < PAGE_SIZE)
    ) {
        // If offset is not a whole number, the end of the results has been reached. (5/11/19) - KS
        isEndOfResults = true;
    }

    selectedLoads.forEach((it) => {
        loadIds.push(it.loadId);
    });

    const handleOnClickAddLoad = () => {
        setModalError(undefined);
        setShowAddModal(true);
    };

    const handleOnClickAddEquipmentModal = () => {
        setModalError(undefined);
        setShowAddEquipmentModal(true);
    };

    const handleOnCancelEquipmentModal = (): void => {
        setModalError(undefined);
        setShowAddEquipmentModal(false);
    };

    const handleOnCancelAddLoad = () => {
        setModalError(undefined);
        setShowAddModal(false);
    };

    const [createAssetLoad, { loading: createAssetLoadLoading, error: createAssetLoadError }] =
        useMutation<CreateAssetLoad>(CREATE_ASSET_LOAD, {
            onCompleted: (data) => {
                handleOnCancelAddLoad();
                const loadIds = (data.createAssetLoad?.loadIds ?? []).filter((id): id is number => id != null);
                toast.success(
                    `Equipment Load${loadIds.length === 1 ? '' : 's'}${loadIds.length ? ` - ${loadIds.join(', ')}` : ''} created successfully`,
                    toastOpt,
                );
            },
            refetchQueries: [
                {
                    query: GET_FILTERED_ASSET_LOAD_MANAGEMENTS,
                    variables: {
                        input: {
                            jobIds: filters.jobIds,
                            regionIds: filters.regionIds,
                            statuses: filters.statuses,
                            loadTypeIds: filters.loadTypeIds,
                            drivers: filters.driverIds,
                            carriers: filters.carrierIds,
                            equipmentId: filters.equipmentId,
                            equipmentTypeIds: filters.equipmentTypeIds,
                            trailerTypeIds: filters.trailerTypeIds,
                            atDestinationOutFrom: filters.fromDate,
                            atDestinationOutTo: filters.toDate,
                            isSearchOnLoadId: searchPhrase ? isSearchOnLoadId : true,
                            search: searchPhrase,
                            sortOrder: '',
                        },
                    },
                },
            ],
            onError: (error) => setModalError(error),
        });

    const handleOnSaveAddLoad = (input: CreateAssetLoadInput) => {
        setModalError(undefined);
        createAssetLoad({
            variables: {
                input: input,
            },
        });
    };

    const [addEquipments, { loading: addEQuipmentMutationLoading }] = useMutation(ADD_EQUIPMENTS, {
        onCompleted: () => handleOnCompleteMutation(),
        onError: (error) => handleOnError(error.message.replace('GraphQL error:', '')),
    });

    const handleOnCompleteMutation = () => {
        toast.success('Added Equipments successfully', toastOpt);
        setShowAddEquipmentModal(false);
    };

    const handleOnError = (errorMessage: string) => {
        toast.error(`Failed to Add Equipments - ${errorMessage}`, toastOpt);
    };

    const handleOnSave = (input: AddEquipmentsInput) => {
        setModalError(undefined);
        addEquipments({
            variables: {
                input: input,
            },
        });
    };

    const handleOnCancelDeleteLoads = (): void => {
        setShowDeleteModal(false);
        setSelectedLoads([]);
    };

    const [deleteAssetLoads] = useMutation<DeleteAssetLoads>(DELETE_ASSET_LOADS, {
        onCompleted: (data) => {
            handleOnCancelDeleteLoads();
            if (data.deleteAssetLoads?.errors && data.deleteAssetLoads?.errors.length !== 0)
                toast.error(data.deleteAssetLoads?.errors[0], toastOpt);
            else toast.success('Selected loads deleted successfully', toastOpt);
        },
        refetchQueries: [
            {
                query: GET_FILTERED_ASSET_LOAD_MANAGEMENTS,
                variables: {
                    input: {
                        jobIds: filters.jobIds,
                        regionIds: filters.regionIds,
                        statuses: filters.statuses,
                        loadTypeIds: filters.loadTypeIds,
                        drivers: filters.driverIds,
                        carriers: filters.carrierIds,
                        equipmentId: filters.equipmentId,
                        equipmentTypeIds: filters.equipmentTypeIds,
                        trailerTypeIds: filters.trailerTypeIds,
                        atDestinationOutFrom: filters.fromDate,
                        atDestinationOutTo: filters.toDate,
                        isSearchOnLoadId: searchPhrase ? isSearchOnLoadId : true,
                        search: searchPhrase,
                        sortOrder: '',
                    },
                },
            },
        ],
    });

    const handleOnClickDeleteLoads = (): void => {
        if (loadIds.length === 0) toast.warning('Please select a load to delete', toastOpt);
        else setShowDeleteModal(true);
    };

    const handleOnSaveDeleteLoads = (): void => {
        const variables = { input: { loadIds } };
        deleteAssetLoads({ variables });
    };

    const handleOnClickAssign = (loadId: number) => {
        setModalError(undefined);
        setLoadId(loadId);
        setShowEditModal(true);
    };

    const handleOnCancelAssign = () => {
        setModalError(undefined);
        setShowEditModal(false);
        setLoadId(undefined);
    };

    const asssetTypes: DropdownOption[] = [];
    const [AssignAssetLoad, { loading: mutationLoading, error: mutationError }] = useMutation<EditAssetLoad>(
        EDIT_ASSET_LOAD,
        {
            onCompleted: (_, input) => {
                handleOnCancelAssign();
                if (!input?.variables?.input?.newValues?.driverId)
                    toast.success('Load updated successfully', toastOpt);
                else
                    toast.success('Load assignment successful', toastOpt);
            },
            refetchQueries: [
                {
                    query: GET_FILTERED_ASSET_LOAD_MANAGEMENTS,
                    variables: {
                        input: {
                            jobIds: filters.jobIds,
                            regionIds: filters.regionIds,
                            statuses: filters.statuses,
                            loadTypeIds: filters.loadTypeIds,
                            drivers: filters.driverIds,
                            carriers: filters.carrierIds,
                            equipmentId: filters.equipmentId,
                            equipmentTypeIds: filters.equipmentTypeIds,
                            trailerTypeIds: filters.trailerTypeIds,
                            atDestinationOutFrom: filters.fromDate,
                            atDestinationOutTo: filters.toDate,
                            isSearchOnLoadId: searchPhrase ? isSearchOnLoadId : true,
                            search: searchPhrase,
                            sortOrder: '',
                        },
                    },
                },
            ],
            onError: (error) => setModalError(error),
        },
    );

    const handleOnSaveAssign = (currentValues: AssetLoadType, newValues: AssetLoadType) => {
        setModalError(undefined);
        AssignAssetLoad({
            variables: {
                input: {
                    assetLoadId: loadId,
                    currentValues: currentValues,
                    newValues: newValues,
                },
            },
        });
    };

    const setListContainerHeight = (): void => {
        const filterDiv = document.getElementById('loadsFilterContainer');
        const listDiv = document.getElementById('loadsListContainer');

        let filterHeight = 0;
        // setTimeout in place to avoid animation miscalculations
        setTimeout(() => {
            if (filterDiv) {
                filterHeight = filterDiv.scrollHeight;
            }
            if (listDiv) {
                setListHeight(Math.min(filterHeight));
            }
        }, 100);
    };
    document.title = 'Equipment Management';

    if (error) {
        return <Error error={error} />;
    }

    if (!isViewLoadEnabled) {
        return (
            <div className="NoResult-Container Inventory-Unauth-Container">
                <div className="NoResult-First-Title-Secondary">Unauthorized</div>
                <div className="NoResult-First-Title-Primary">You do not have permission to view this screen</div>
            </div>
        );
    }

    const [exportDataCSV, { data: exportedDataForDownload, loading: exportDataLoading }] = useLazyQuery<GetExportDataForDownload>(
        GET_ASSETS_FOR_EXPORT_DATA_DOWNLOAD,
        { fetchPolicy: 'network-only' },
    );

    useEffect(() => {
        if (!exportDataLoading && exportedDataForDownload) {
            downloadHandlerForExportDataCSV(exportedDataForDownload);
        }
    }, [exportedDataForDownload, exportDataLoading]);

    const extractFilterValues = (filterValues) => {
        const dataValues = Object.values(filterValues);
        const filterHeader: any = [];
        if (dataValues && dataValues.length == 3) {
            const filterKey: any = dataValues[1];

            filterHeader[filterKey] = dataValues[2];
        }
        return `${dataValues[1]} : ${dataValues[2]}`;
    };

    const fetchFiltersValues = (data, csv) => {
        data?.filterValues.map((items) => {
            const filters = extractFilterValues(items);
            csv.unshift(filters);
        });

        return csv;
    };

    const downloadHandlerForExportDataCSV = (data) => {
        if (data && data?.getExportDataForDownload) {
            const data1 = data?.getExportDataForDownload?.loads.map((assets) => {
                return {
                    'Date': assets?.atDestinationOut ? moment(assets?.atDestinationOut).format("YYYY/MM/DD") : '',
                    'CN': assets?.loadId,
                    'Name': assets?.driverName,
                    'Truck': assets?.truckNumber,
                    'Kronos Paycode': assets?.payCode,
                    'Equipment Type': assets?.equipments,
                    'Equipment Id': assets?.equipmentDetails,
                    'Description/Notes': assets?.comments,
                    'Stepdeck Miles': assets?.miles !== 0 ? assets?.miles : '',// changes to be made in graphQl to handle miles value if it is 0.
                    'Region': assets?.districtAbbreviation,
                    'Load Type': assets?.assetLoadType,
                    'Load Status': assets?.status,
                    'Origin In': assets?.atOriginIn ? moment(assets?.atOriginIn).format("YYYY/MM/DD") : '',
                    'Origin Out': assets?.atOriginOut ? moment(assets?.atOriginOut).format("YYYY/MM/DD") : '',
                    'Destination In': assets?.atDestinationIn ? moment(assets?.atDestinationIn).format("YYYY/MM/DD") : '',
                    'Destination Out': assets?.atDestinationOut ? moment(assets?.atDestinationOut).format("YYYY/MM/DD") : '',
                    'Carrier': assets?.carrierName,
                };
            });

            const replacer = (key, value) => (value === null ? '' : value);
            const header = Object.keys(data1[0]);

            let csv = data1.map((row) => header.map((fieldName) => JSON.stringify(row[fieldName], replacer)).join(','));
            csv.unshift(header.join(','));
            fetchFiltersValues(data?.getExportDataForDownload, csv);
            csv = csv.join('\r\n');

            const blob = new Blob(['\ufeff', csv]);
            const url = window.URL.createObjectURL(blob);
            const downloadLink = document.createElement('a');
            downloadLink.href = url;
            downloadLink.download = 'ExportToData.csv'; //Name the file here
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        }
    };

    const onExportDataCSV = () => {
        exportDataCSV({
            variables: {
                input: {
                    jobIds: filters.jobIds,
                    regionIds: filters.regionIds,
                    statuses: filters.statuses,
                    loadTypeIds: filters.loadTypeIds,
                    drivers: filters.driverIds,
                    carriers: filters.carrierIds,
                    equipmentId: filters.equipmentId,
                    equipmentTypeIds: filters.equipmentTypeIds,
                    trailerTypeIds: filters.trailerTypeIds,
                    atDestinationOutFrom: filters.fromDate,
                    atDestinationOutTo: filters.toDate,
                    isSearchOnLoadId: searchPhrase ? isSearchOnLoadId : true,
                    search: searchPhrase,
                    sortOrder: '',
                    isKronosExport: false,
                    isMobFormExport: 0,
                },
            },
        });
    }

    const downloadHandlerForKronosDataCSV = (data) => {
        if (data && data?.getAssetLoadListToDownload) {
            const data1 = data?.getAssetLoadListToDownload.map(assets => {
                return {
                    'Employee Id': assets?.kronosId,
                    'Pay Date': assets?.atDestinationOut ? moment(assets?.atDestinationOut).format("YYYY/MM/DD") : '',
                    'Piece Quantity': '1',
                    'Cost Center 3': assets?.districtAbbreviation,
                    'Cost Center 8': assets?.miles !== 0 ? assets?.miles : '',// changes to be made in graphQl to handle miles value if it is 0.
                    'Cost Center 9': assets?.payCode,
                    'Custom 1': assets?.loadId,
                    'Note': assets?.comments
                };
            });

            const opts = {};
            const parser = new Parser(opts);
            const csvData = parser.parse(data1);
            const file = new Blob([csvData], { type: 'application/csv' });
            const url = window.URL.createObjectURL(file);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ExportKronosData.csv`;
            a.click();
        }
    };

    const [exportKronosToDataCSV, { data: KronosDataForDownload, loading: KronosDataForDownloadLoading }] =
        useLazyQuery<GetAssetLoadListToDownload>(GET_ASSETS_FOR_DOWNLOAD, {
            fetchPolicy: 'network-only',
        });

    useEffect(() => {
        if (!KronosDataForDownloadLoading && KronosDataForDownload) {
            if ((KronosDataForDownload.getAssetLoadListToDownload?.length ?? 0) > 0) {
                downloadHandlerForKronosDataCSV(KronosDataForDownload);
            } else {
                toast.error('No loads meet the criteria for Kronos Export', toastOpt);
            }
        }
    }, [KronosDataForDownload, KronosDataForDownloadLoading]);

    const onExportToKronosCSV = () => {
        exportKronosToDataCSV({
            variables: {
                input: {
                    jobIds: filters.jobIds,
                    regionIds: filters.regionIds,
                    statuses: filters.statuses,
                    loadTypeIds: filters.loadTypeIds,
                    drivers: filters.driverIds,
                    carriers: filters.carrierIds,
                    equipmentId: filters.equipmentId,
                    equipmentTypeIds: filters.equipmentTypeIds,
                    trailerTypeIds: filters.trailerTypeIds,
                    atDestinationOutFrom: filters.fromDate,
                    atDestinationOutTo: filters.toDate,
                    isSearchOnLoadId: searchPhrase ? isSearchOnLoadId : true,
                    search: searchPhrase,
                    sortOrder: '',
                    isKronosExport: true,
                    isMobFormExport: 0,
                },
            },
        });
    };

    const [exportToMobDemobFormCSV, { data: MobDemobFormData, loading: MobDemobFormLoding }] = useLazyQuery<GetMobDemobFormExportDownload>(
        GET_ASSETS_FOR_MOB_DEMOB_FORM_DOWNLOAD,
        { fetchPolicy: 'network-only' },
    );

    useEffect(() => {
        if (!MobDemobFormLoding && MobDemobFormData) {
            downloadHandlerForMobDemobFormDataCSV(MobDemobFormData);
        }
    }, [MobDemobFormData, MobDemobFormLoding]);

    const downloadHandlerForMobDemobFormDataCSV = (data) => {
        if (data && data?.getMobDemobFormExportDownload) {
            const data1 = data?.getMobDemobFormExportDownload.map((assets, index) => {
                return {
                    'No.': ++index,
                    'Customer Name': assets?.customer,
                    'Job Name': assets?.jobName,
                    'Date': assets?.atDestinationOut ? moment(assets?.atDestinationOut).format("YYYY/MM/DD") : '',
                    'Load Number': assets?.loadId,
                    'Equipment Type': assets?.equipments,
                    'Quantity': assets?.quantity,
                    'Origin Name': assets?.originAddress,
                    'Destination Name': assets?.destinationAddress,
                    'Mileage': assets?.miles !== 0 ? assets?.miles : '', // changes to be made in graphQl to handle miles value if it is 0.
                };
            });

            const opts = {};
            const parser = new Parser(opts);
            const csvData = parser.parse(data1);
            const file = new Blob([csvData], { type: 'application/csv' });
            const url = window.URL.createObjectURL(file);
            const a = document.createElement('a');
            a.href = url;
            a.download = `MobDemobFormData.csv`;
            a.click();
        }
    };

    const onExportToMobDemobFormCSV = () => {
        exportToMobDemobFormCSV({
            variables: {
                input: {
                    jobIds: filters.jobIds,
                    regionIds: filters.regionIds,
                    statuses: filters.statuses,
                    loadTypeIds: filters.loadTypeIds,
                    drivers: filters.driverIds,
                    carriers: filters.carrierIds,
                    equipmentId: filters.equipmentId,
                    equipmentTypeIds: filters.equipmentTypeIds,
                    trailerTypeIds: filters.trailerTypeIds,
                    atDestinationOutFrom: filters.fromDate,
                    atDestinationOutTo: filters.toDate,
                    isSearchOnLoadId: searchPhrase ? isSearchOnLoadId : true,
                    search: searchPhrase,
                    sortOrder: '',
                    isKronosExport: false,
                    isMobFormExport: 0,
                },
            },
        });
    };

    return (
        <div className="AssetLoadsManagement-Container">
            <AssetLoadManagementHeader
                onCheckAll={(checked) => onCheckAll(checked, assetLoadList)}
                selectedLoads={selectedLoads}
                openSelectedLoads={() => { }}
                deleteSelectedLoads={handleOnClickDeleteLoads}
                addLoad={handleOnClickAddLoad}
                addEquipmentModal={handleOnClickAddEquipmentModal}
                isSearchOnLoadId={isSearchOnLoadId}
                setIsSearchOnLoadId={setIsSearchOnLoadId}
                showFilter={showFilter}
                setShowFilter={setShowFilter}
                onDownload={onExportDataCSV}
                onExportToKronos={onExportToKronosCSV}
                onExportToMobDemobForm={onExportToMobDemobFormCSV}
                loading={exportDataLoading || KronosDataForDownloadLoading || MobDemobFormLoding}
            />

            <ToastContainer position="top-center" />

            <div className="AssetLoadsManagement-BodyContainer">
                <div className="JobLoads-Container">
                    <div className="JobLoads-BodyContainer">
                        <AssetLoadFilters
                            abortRequest={abortRequest}
                            onFilterToggle={setListContainerHeight}
                            showFilter={showFilter}
                            setShowFilter={setShowFilter}
                        />
                    </div>
                </div>

                <div
                    id="loadsListContainer"
                    className="AssetLoadsManagement-ListContainer"
                    style={{ height: listHeight }}
                >
                    <AssetLoadList
                        assetLoadList={assetLoadList}
                        isLoading={loading}
                        isEndOfResults={isEndOfResults}
                        entries={(data && data.getFilteredAssetLoadManagements) || []}
                        selectedLoads={selectedLoads}
                        setSelectedLoads={setSelectedLoads}
                        onClickAssign={handleOnClickAssign}
                        onLoadMore={() =>
                            fetchMore({
                                variables: {
                                    offset,
                                },
                                updateQuery: (prev: any, { fetchMoreResult }: any) => {
                                    if (
                                        prev &&
                                        fetchMoreResult &&
                                        prev.getFilteredAssetLoadManagements &&
                                        fetchMoreResult.getFilteredAssetLoadManagements
                                    ) {
                                        return Object.assign({}, prev, {
                                            getFilteredAssetLoadManagements: [
                                                ...prev.getFilteredAssetLoadManagements,
                                                ...fetchMoreResult.getFilteredAssetLoadManagements,
                                            ],
                                        });
                                    } else {
                                        return prev;
                                    }
                                },
                            })
                        }
                    />
                </div>

                {isAddLoadEnabled && showAddModal && (
                    <AddAssetLoadModal
                        loadType="New"
                        showModal={showAddModal}
                        loading={createAssetLoadLoading}
                        error={modalError}
                        onClickCancel={handleOnCancelAddLoad}
                        onClickSaveForAdd={handleOnSaveAddLoad}
                    />
                )}

                {isAddEquipmentsEnabled && showAddEquipmentModal && (
                    <AddEquipmentModal
                        showModal={showAddEquipmentModal}
                        loading={false}
                        error={modalError}
                        onClickCancel={handleOnCancelEquipmentModal}
                        onClickSave={handleOnSave}
                    />
                )}

                {isDeleteLoadEnabled && showDeleteModal && (
                    <DeleteModal
                        show={showDeleteModal}
                        onCancel={handleOnCancelDeleteLoads}
                        onConfirm={handleOnSaveDeleteLoads}
                        name="asset load"
                    />
                )}

                {isAssignLoadEnabled && showEditModal && (
                    <AddAssetLoadModal
                        loadType="Edit"
                        loadId={loadId}
                        showModal={showEditModal}
                        loading={mutationLoading}
                        error={modalError}
                        onClickCancel={handleOnCancelAssign}
                        onClickSaveForEdit={handleOnSaveAssign}
                    />
                )}
            </div>
        </div>
    );
};

export default EquipmentManagement;
